import { ArrowLeft, ChevronDown } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PortalFooter } from "../components/PortalFooter";
import { appConfig } from "../config/appConfig";
import { getPublicPracticeOptions, submitAccessRequest, type PracticeOption } from "../lib/portalApi";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

type RegisterPageProps = {
  onBackToLogin: () => void;
};

type AccessRequestForm = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedPracticeId: string;
  practiceName: string;
  licenseNumber: string;
  licenseState: string;
  termsAccepted: boolean;
};

const manualPracticeValue = "__manual_practice__";

const stateOptions = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

const emptyAccessRequest: AccessRequestForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  selectedPracticeId: "",
  practiceName: "",
  licenseNumber: "",
  licenseState: "",
  termsAccepted: false
};

function phoneDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10);
}

function formatPhone(value: string): string {
  const digits = phoneDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function AccessRequestPage({ onBackToLogin }: RegisterPageProps) {
  const [accessRequest, setAccessRequest] = useState<AccessRequestForm>(emptyAccessRequest);
  const [practiceOptions, setPracticeOptions] = useState<PracticeOption[]>([]);
  const [practiceOptionsError, setPracticeOptionsError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [submittedCode, setSubmittedCode] = useState<string | null>(null);
  const [submittedDuplicateBy, setSubmittedDuplicateBy] = useState<"email" | "license" | "unknown" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clearTurnstileToken = useCallback(() => setTurnstileToken(""), []);

  const selectedPractice = useMemo(
    () => practiceOptions.find((practice) => practice.id === accessRequest.selectedPracticeId) ?? null,
    [accessRequest.selectedPracticeId, practiceOptions]
  );
  const selectedState = useMemo(
    () => stateOptions.find((state) => state.value === accessRequest.licenseState) ?? null,
    [accessRequest.licenseState]
  );
  const practiceComboOptions = useMemo(
    () => [
      { value: manualPracticeValue, label: "My practice is not listed", searchText: "my practice is not listed" },
      ...practiceOptions.map((practice) => ({
        value: practice.id,
        label: practice.label,
        searchText: `${practice.name} ${practice.address} ${practice.label}`
      }))
    ],
    [practiceOptions]
  );
  const isManualPractice = accessRequest.selectedPracticeId === manualPracticeValue;
  const selectedPracticeLabel = isManualPractice ? "My practice is not listed" : selectedPractice?.label ?? "";

  useEffect(() => {
    let isMounted = true;

    getPublicPracticeOptions()
      .then((options) => {
        if (isMounted) setPracticeOptions(options);
      })
      .catch(() => {
        if (isMounted) {
          setPracticeOptionsError(
            "Practice options are not available right now. You can still choose that your practice is not listed."
          );
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function updateField<Key extends keyof AccessRequestForm>(field: Key, value: AccessRequestForm[Key]) {
    setAccessRequest((current) => ({ ...current, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitted) return;
    setIsSubmitting(true);
    setStatus(null);
    setSubmittedCode(null);
    setSubmittedDuplicateBy(null);
    setError(null);

    try {
      const practiceName = isManualPractice ? accessRequest.practiceName.trim() : selectedPractice?.name ?? "";
      if (phoneDigits(accessRequest.phone).length !== 10) {
        setError("Please enter a 10-digit phone number.");
        return;
      }
      if (!accessRequest.selectedPracticeId || !practiceName) {
        setError("Please select a practice or choose that your practice is not listed.");
        return;
      }
      if (!accessRequest.licenseState) {
        setError("Please select a state of licensure.");
        return;
      }
      if (!turnstileToken) {
        setError("Please complete the verification challenge.");
        return;
      }
      if (!accessRequest.termsAccepted) {
        setError("Please agree to the Terms of Service and Privacy Policy.");
        return;
      }
      const result = await submitAccessRequest({
        firstName: accessRequest.firstName,
        lastName: accessRequest.lastName,
        email: accessRequest.email,
        phone: accessRequest.phone,
        practiceId: isManualPractice ? undefined : selectedPractice?.id,
        practiceMode: isManualPractice ? "manual" : "existing",
        practiceName,
        licenseNumber: accessRequest.licenseNumber,
        licenseState: accessRequest.licenseState,
        turnstileToken,
        termsAccepted: accessRequest.termsAccepted
      });
      setStatus(result.message);
      setSubmittedCode(result.code ?? "submitted");
      setSubmittedDuplicateBy(result.duplicateBy ?? null);
      setAccessRequest(emptyAccessRequest);
      setTurnstileToken("");
      setTurnstileKey((current) => current + 1);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError("We could not submit your request. Please review the fields and try again.");
      setTurnstileToken("");
      setTurnstileKey((current) => current + 1);
    } finally {
      setIsSubmitting(false);
    }
  }

  const submittedEyebrow = submittedCode === "already_registered"
    ? "Account Found"
    : submittedCode === "registration_email_resent"
      ? "Registration Email Sent"
      : submittedCode === "already_submitted"
        ? "Request Already Submitted"
        : "Request Submitted";
  const submittedTitle = submittedCode === "already_registered"
    ? "This account already exists"
    : submittedCode === "registration_email_resent"
      ? "Check your email"
      : submittedCode === "already_submitted"
        ? submittedDuplicateBy === "license"
          ? "A request for this license is already under review"
          : "Your request is already under review"
        : "We received your request";
  const submittedGuidance = submittedCode === "already_submitted" && submittedDuplicateBy === "license"
    ? "This does not mean this email address already has a portal account. It means the license number and state are already tied to an access request awaiting review."
    : "To submit another request, please start a new access request from the sign-in page.";

  return (
    <main className="register-page">
      {appConfig.showTestBanner ? <div className="test-banner">TEST ENVIRONMENT</div> : null}

      <section className="register-shell">
        <a
          className="inline-back-button"
          href="/login"
          onClick={(event) => {
            event.preventDefault();
            onBackToLogin();
          }}
        >
          <ArrowLeft size={18} aria-hidden="true" />
          Back to sign in
        </a>

        <div className="register-grid">
          <section className="register-intro" aria-labelledby="register-heading">
            <img
              src="https://vp-brand-assets.s3.amazonaws.com/companion-pharmacy/companion_pharmacy_logo_primary.png"
              alt="Companion Pharmacy"
            />
            <p className="eyebrow">Prescriber Portal</p>
            <h1 id="register-heading">Request portal access</h1>
            <p>Submit your information and our team will review the request before enabling portal access.</p>
            <p className="register-login-link">
              Already have an account?{" "}
              <a
                className="inline-link-button"
                href="/login"
                onClick={(event) => {
                  event.preventDefault();
                  onBackToLogin();
                }}
              >
                Sign in
              </a>
            </p>
          </section>

          <section className="register-panel" aria-label="Access request form">
            {isSubmitted ? (
              <div className="access-request-success-panel" role="status">
                <div className="section-header">
                  <div>
                    <p className="eyebrow">{submittedEyebrow}</p>
                    <h2>{submittedTitle}</h2>
                  </div>
                </div>
                <p>
                  {status ??
                    "Your portal access request has been submitted. Our team will review it and email you with next steps."}
                </p>
                <p className="field-note">{submittedGuidance}</p>
                <div className="button-row access-request-success-actions">
                  <a
                    className="primary-button"
                    href="/login"
                    onClick={(event) => {
                      event.preventDefault();
                      onBackToLogin();
                    }}
                  >
                    Back to sign in
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
              <div className="section-header">
                <div>
                  <p className="eyebrow">First Time Here?</p>
                </div>
              </div>

              <div className="two-column-form">
                <label>
                  First name
                  <input
                    autoComplete="given-name"
                    value={accessRequest.firstName}
                    onChange={(event) => updateField("firstName", event.target.value)}
                    required
                  />
                </label>
                <label>
                  Last name
                  <input
                    autoComplete="family-name"
                    value={accessRequest.lastName}
                    onChange={(event) => updateField("lastName", event.target.value)}
                    required
                  />
                </label>
              </div>

              <div className="two-column-form">
                <label>
                  Email address
                  <input
                    type="email"
                    autoComplete="email"
                    value={accessRequest.email}
                    onChange={(event) => updateField("email", event.target.value)}
                    required
                  />
                </label>
                <label>
                  Phone
                  <input
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
                    placeholder="(555) 555-5555"
                    value={accessRequest.phone}
                    onChange={(event) => updateField("phone", formatPhone(event.target.value))}
                    required
                  />
                </label>
              </div>

              <label>
                Practice name
                <SearchableSelect
                  options={practiceComboOptions}
                  placeholder="Search for a practice..."
                  value={accessRequest.selectedPracticeId}
                  valueLabel={selectedPracticeLabel}
                  onChange={(selectedPracticeId) => {
                    setAccessRequest((current) => ({
                      ...current,
                      selectedPracticeId,
                      practiceName: selectedPracticeId === manualPracticeValue ? current.practiceName : ""
                    }));
                  }}
                />
              </label>
              {practiceOptionsError ? <p className="field-note warning">{practiceOptionsError}</p> : null}
              {isManualPractice ? (
                <label>
                  Practice name if not listed
                  <input
                    value={accessRequest.practiceName}
                    onChange={(event) => updateField("practiceName", event.target.value)}
                    required
                  />
                </label>
              ) : null}

              <div className="two-column-form license-row">
                <label>
                  <span className="field-label-text">Veterinary medical board license number</span>
                  <input
                    value={accessRequest.licenseNumber}
                    onChange={(event) => updateField("licenseNumber", event.target.value)}
                    required
                  />
                </label>
                <label>
                  <span className="field-label-text">State of licensure</span>
                  <SearchableSelect
                    options={stateOptions.map((state) => ({
                      value: state.value,
                      label: `${state.value} - ${state.label}`,
                      searchText: `${state.value} ${state.label}`
                    }))}
                    placeholder="Search state..."
                    value={accessRequest.licenseState}
                    valueLabel={selectedState ? `${selectedState.value} - ${selectedState.label}` : ""}
                    onChange={(value) => updateField("licenseState", value)}
                  />
                </label>
              </div>

              <TurnstileBox
                key={turnstileKey}
                siteKey={appConfig.turnstileSiteKey}
                onToken={setTurnstileToken}
                onExpire={clearTurnstileToken}
              />

              <label className="agreement-row">
                <input
                  type="checkbox"
                  checked={accessRequest.termsAccepted}
                  onChange={(event) => updateField("termsAccepted", event.target.checked)}
                  required
                />
                <span>
                  I agree to the{" "}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>

              {status ? <div className="form-success">{status}</div> : null}
              {error ? <div className="form-alert">{error}</div> : null}

              <div className="button-row">
                <button className="primary-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit access request"}
                </button>
                <a
                  className="secondary-button"
                  href="/login"
                  onClick={(event) => {
                    event.preventDefault();
                    onBackToLogin();
                  }}
                >
                  Sign in instead
                </a>
              </div>
            </form>
            )}
          </section>
        </div>
      </section>

      <PortalFooter />
    </main>
  );
}

function SearchableSelect({
  options,
  placeholder,
  value,
  valueLabel,
  onChange
}: {
  options: Array<{ value: string; label: string; searchText?: string }>;
  placeholder: string;
  value: string;
  valueLabel: string;
  onChange: (value: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const controlRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const visibleOptions = useMemo(
    () =>
      options
        .filter((option) => {
          if (!normalizedQuery) return true;
          return `${option.label} ${option.searchText ?? ""}`.toLowerCase().includes(normalizedQuery);
        })
        .slice(0, 80),
    [normalizedQuery, options]
  );

  function displayValue() {
    if (isOpen) return query;
    return valueLabel;
  }

  function openMenu() {
    const rect = controlRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpensUp(spaceBelow < 220 && spaceAbove > spaceBelow);
    }
    setIsOpen(true);
  }

  function selectOption(optionValue: string) {
    setQuery("");
    setIsOpen(false);
    onChange(optionValue);
    window.requestAnimationFrame(() => {
      setIsOpen(false);
      inputRef.current?.blur();
    });
  }

  return (
    <div className={opensUp ? "searchable-select open-up" : "searchable-select"}>
      <div className="searchable-select-control" ref={controlRef}>
        <input
          ref={inputRef}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          autoComplete="off"
          placeholder={placeholder}
          value={displayValue()}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
          onChange={(event) => {
            setQuery(event.target.value);
            openMenu();
          }}
          onFocus={() => {
            setQuery("");
            openMenu();
          }}
          required={!value}
        />
        <span className="searchable-select-toggle" aria-hidden="true">
          <ChevronDown size={18} />
        </span>
      </div>
      {isOpen ? (
        <div className="searchable-select-menu" role="listbox">
          {visibleOptions.length ? (
            visibleOptions.map((option) => (
              <button
                className={option.value === value ? "searchable-select-option selected" : "searchable-select-option"}
                type="button"
                role="option"
                aria-selected={option.value === value}
                key={option.value}
                onPointerDown={(event) => {
                  event.preventDefault();
                  selectOption(option.value);
                }}
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.preventDefault();
                  if (event.detail === 0) {
                    selectOption(option.value);
                  }
                }}
              >
                {option.label}
              </button>
            ))
          ) : (
            <p className="searchable-select-empty">No matches found.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

function TurnstileBox({
  siteKey,
  onExpire,
  onToken
}: {
  siteKey: string;
  onExpire: () => void;
  onToken: (token: string) => void;
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!container || !siteKey) return undefined;
    const turnstileContainer = container;
    let widgetId = "";
    let isCancelled = false;

    function renderWidget() {
      if (isCancelled || !window.turnstile || widgetId) return;
      widgetId = window.turnstile.render(turnstileContainer, {
        sitekey: siteKey,
        theme: "light",
        callback: onToken,
        "expired-callback": onExpire,
        "error-callback": () => {
          setError("Verification could not be completed. Please try again.");
          onExpire();
        }
      });
    }

    if (window.turnstile) {
      renderWidget();
    } else {
      const existingScript = document.querySelector<HTMLScriptElement>("script[data-cppp-turnstile]");
      const script = existingScript ?? document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.cpppTurnstile = "true";
      script.addEventListener("load", renderWidget);
      if (!existingScript) document.head.appendChild(script);
    }

    return () => {
      isCancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [container, onExpire, onToken, siteKey]);

  if (!siteKey) {
    return <p className="field-note warning">Verification is not configured for this environment.</p>;
  }

  return (
    <div className="turnstile-field">
      <div ref={setContainer} />
      {error ? <p className="error-text">{error}</p> : null}
    </div>
  );
}
